import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"

export default class extends Controller {
  static targets = ["input", "submit", "messages"]
  static values = {
    sessionId: String
  }

  connect() {
    // Configure marked for safe markdown rendering
    marked.setOptions({
      breaks: true,        // Support line breaks
      gfm: true,           // GitHub Flavored Markdown
      headerIds: false,    // No IDs in headers
      mangle: false        // No email obfuscation
    })

    this.loadConversationHistory()
    this.scrollToBottom()
  }

  // Load conversation from localStorage
  loadConversationHistory() {
    const sessionKey = this.getSessionKey()
    const savedMessages = localStorage.getItem(sessionKey)

    if (savedMessages) {
      try {
        const messages = JSON.parse(savedMessages)
        // Messages are already in the DOM (rendered by Rails)
        // This is just for future reference if we need to restore
        this.conversationHistory = messages
      } catch (e) {
        console.error('Error loading conversation history:', e)
        this.conversationHistory = []
      }
    } else {
      this.conversationHistory = []
    }
  }

  // Get unique key for this chat session
  getSessionKey() {
    return `demo_chat_${this.sessionIdValue}`
  }

  // Save message to localStorage
  saveMessage(role, content) {
    if (!this.conversationHistory) {
      this.conversationHistory = []
    }

    const message = {
      role: role,
      content: content,
      timestamp: new Date().toISOString()
    }

    this.conversationHistory.push(message)

    const sessionKey = this.getSessionKey()
    localStorage.setItem(sessionKey, JSON.stringify(this.conversationHistory))
  }

  // Clear conversation from localStorage (called when completed)
  clearConversation() {
    const sessionKey = this.getSessionKey()
    localStorage.removeItem(sessionKey)
    this.conversationHistory = []
  }

  submit(event) {
    // Prevent default form submission - we'll handle it manually
    event.preventDefault()

    const input = this.inputTarget
    const messageText = input.value.trim()

    console.log('Submit triggered, message:', messageText)

    if (messageText === '') {
      console.log('Empty message, aborting')
      return
    }

    // Save user message to localStorage immediately
    this.saveMessage('user', messageText)

    // Add user message to DOM immediately for instant feedback
    this.addMessageToDOM('user', messageText)

    // Prepare FormData BEFORE clearing input
    const form = event.target
    const formData = new FormData(form)
    formData.set('message', messageText)  // Ensure message is set

    // NOW clear and disable input (force multiple methods)
    input.value = ''
    input.setAttribute('value', '')
    form.reset()
    input.disabled = true
    input.classList.add('bg-gray-100', 'cursor-not-allowed')
    input.placeholder = 'En attente de la réponse...'

    // Disable submit button
    this.submitTarget.disabled = true
    this.submitTarget.classList.add('opacity-50', 'cursor-not-allowed')

    // Store the message text
    this.lastMessage = messageText

    // Get CSRF token from meta tag
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Submit via Turbo
    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken
      }
    })
    .then(response => response.text())
    .then(html => {
      // Let Turbo process the response
      Turbo.renderStreamMessage(html)

      // Trigger our cleanup
      this.turboStreamConnect()
    })
    .catch(error => {
      console.error('Error submitting message:', error)

      // Re-enable input on error and restore message
      input.disabled = false
      input.classList.remove('bg-gray-100', 'cursor-not-allowed')
      input.placeholder = 'Tapez votre message...'
      input.value = messageText  // Restore the message so user can retry
      input.focus()

      // Re-enable submit button
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    })
  }

  // Add message to DOM
  addMessageToDOM(role, content) {
    const messagesContainer = document.getElementById('chat-messages')
    if (!messagesContainer) return

    const messageDiv = document.createElement('div')
    messageDiv.className = `flex items-start space-x-3 ${role === 'user' ? 'flex-row-reverse space-x-reverse' : ''} animate-fade-in`
    messageDiv.setAttribute('data-message-role', role)

    const avatarDiv = document.createElement('div')
    avatarDiv.className = `w-8 h-8 ${role === 'user' ? 'bg-gray-300' : 'bg-[#2c3f51]'} rounded-full flex items-center justify-center flex-shrink-0`
    avatarDiv.innerHTML = `<span class="text-white text-xs font-bold">${role === 'user' ? '👤' : 'AS'}</span>`

    const contentContainer = document.createElement('div')
    contentContainer.className = 'flex-1 max-w-md'

    const bubble = document.createElement('div')
    bubble.className = `${role === 'user' ? 'bg-gray-200 ml-auto' : 'bg-white shadow-sm'} rounded-lg p-4`

    // Parse markdown for agent messages, plain text for user messages
    if (role === 'agent' || role === 'assistant') {
      bubble.innerHTML = `<div class="text-gray-900 prose prose-sm max-w-none">${marked.parse(content)}</div>`
    } else {
      bubble.innerHTML = `<div class="text-gray-900">${this.escapeHtml(content).replace(/\n/g, '<br>')}</div>`
    }

    const timestamp = document.createElement('div')
    timestamp.className = `text-xs text-gray-500 mt-1 ${role === 'user' ? 'text-right' : ''}`
    timestamp.textContent = "À l'instant"

    contentContainer.appendChild(bubble)
    contentContainer.appendChild(timestamp)

    messageDiv.appendChild(avatarDiv)
    messageDiv.appendChild(contentContainer)

    messagesContainer.appendChild(messageDiv)

    this.smoothScrollToBottom()
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Called after Turbo Stream response is received
  turboStreamConnect(event) {
    // Extract agent response from the DOM and save to localStorage
    const allMessages = document.querySelectorAll('[data-message-role]')
    if (allMessages.length > 0) {
      const lastMessage = allMessages[allMessages.length - 1]
      if (lastMessage.getAttribute('data-message-role') === 'agent') {
        const content = lastMessage.querySelector('.text-gray-900')?.textContent
        if (content) {
          this.saveMessage('agent', content)
        }
      }
    }

    // Re-enable input after Turbo Stream updates
    if (this.hasInputTarget && this.lastMessage) {
      this.inputTarget.value = ''  // Ensure it's clear
      this.inputTarget.disabled = false
      this.inputTarget.classList.remove('bg-gray-100', 'cursor-not-allowed')
      this.inputTarget.placeholder = 'Tapez votre message...'
      this.inputTarget.focus()
      this.lastMessage = null
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }

    this.scrollToBottom()
  }

  scrollToBottom() {
    const messages = document.getElementById('chat-messages')
    if (messages) {
      messages.scrollTop = messages.scrollHeight
    }
  }

  // Smooth scroll to bottom with animation
  smoothScrollToBottom() {
    const messages = document.getElementById('chat-messages')
    if (messages) {
      messages.scrollTo({
        top: messages.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  // Called when Turbo Stream updates arrive
  messagesTargetConnected() {
    this.smoothScrollToBottom()
  }
}
